import * as types from "_graphql-types-frontend/graphql";
import classNames from "classnames";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { capitalize } from "lodash";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { useFieldValue, useTemplateStoreFromContext } from "../Context/hooks";
import { isHTML } from "../Static/TextStatic";
import { Field } from "../types";
import { CopyField } from "./CopyField";
import { FieldStamp } from "./FieldAuditStamp";
import { useShallow } from "zustand/react/shallow";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CitationDisplay from "./CitationDisplay";

type StateKey = "draft" | "mgr" | "published" | "rangerGenerated";

interface TabContent {
  stateKey: StateKey;
  field: Field;
}

const fieldState = {
  mgr: "Manager",
  draft: "Draft",
  published: "Published",
  rangerGenerated: (
    <>
      <AutoAwesomeIcon style={{ fontSize: "12px", margin: 0 }} /> Sirius AI
    </>
  ),
};

const Expander: React.FC<{
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  contentHeight: number;
}> = ({ expanded, setExpanded, contentHeight }) => {
  if (contentHeight < 70) return null;
  const iconClasses = classNames({
    "switch-btn__icon": true,
    icon: true,
    "icon--20": true,
    "icon-arrow-up": expanded,
    "icon-arrow": !expanded,
  });

  const text = expanded ? "Show Less" : "Show More";

  return (
    <button
      type="button"
      className="switch-btn"
      onClick={() => setExpanded(!expanded)}
    >
      {text}
      <i className={iconClasses} />
    </button>
  );
};

const sandwichNotPublished = {
  mgr: "No Manager's updated yet",
  published: "Nothing Published",
  draft: "Nothing edited",
  rangerGenerated: "No Sirius AI answer yet",
};

function RenderPanel({
  stateKey = "published",
  field,
  expanded,
  setExpanded,
  contentRef,
}: {
  stateKey: StateKey;
  field: Field;
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  contentRef: React.RefObject<HTMLDivElement>;
}) {
  const tabName = fieldState[stateKey];
  const classes = classNames({
    "cms-tabs__txt": true,
    "cms-tabs__txt--expanded": expanded,
  });

  // this is to ensure this component is re-rendered when the content height changes
  // example: when ranger has a longer answer then published.
  const [contentHeight, setContentHeight] = useState(0);

  const updateHeight = () => {
    if (contentRef?.current) {
      setContentHeight(contentRef.current.clientHeight);
    }
  };

  useEffect(() => {
    const observer = new ResizeObserver(updateHeight);
    if (contentRef.current) {
      observer.observe(contentRef.current);
    }
  }, [contentRef]);

  console.log(field?.citations);

  return (
    <>
      {(!field || !field.value) && (
        <div className="text-center in-disabled-blue pt-8 pb-45" key={stateKey}>
          <i className="icon icon-notes d-inline-block font-32 mb-8" />
          <p>{sandwichNotPublished[stateKey]}</p>
        </div>
      )}
      {field && field.value && (
        <>
          <div key={field?.fieldKey} className={classes}>
            <div ref={contentRef} className="rating-txt">
              {(field.notApplicable && <i>N/A</i>) || isHTML(field.value) ? (
                <ReactQuill
                  theme="bubble"
                  value={field.value ?? ""}
                  readOnly={true}
                />
              ) : (
                field.value
              )}
            </div>
          </div>
          <div
            className="d-flex justify-between flex-wrap"
            key={`${stateKey}(field_info)`}
          >
            <div>
              <Expander
                expanded={expanded}
                setExpanded={setExpanded}
                contentHeight={contentRef.current?.clientHeight ?? 0}
              />
              <div className="d-flex flex-wrap">
                <FieldStamp field={field} />
                <CopyField field={field} />
              </div>
            </div>
            <div>
              {field.citations && (
                <div className="text-gray-500 font-12 mt-4">
                  Citations: <CitationDisplay citations={field.citations} />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

// Memoized individual tab panel to prevent unnecessary re-renders
const MemoizedTabPanel = memo<{
  stateKey: StateKey;
  field: Field;
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  contentRef: React.RefObject<HTMLDivElement>;
  children?: React.ReactNode;
}>(({ stateKey, field, expanded, setExpanded, contentRef, children }) => (
  <>
    <RenderPanel
      stateKey={stateKey}
      field={field}
      expanded={expanded}
      setExpanded={setExpanded}
      contentRef={contentRef}
    />
    {children}
  </>
));

// Memoized tabs component
const MemoizedTabs = memo<{
  tabs: TabContent[];
  defaultTabIndex: number;
  onTabSelect: (index: number) => void;
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  contentRef: React.RefObject<HTMLDivElement>;
  children?: React.ReactNode;
}>(
  ({
    tabs,
    defaultTabIndex,
    onTabSelect,
    expanded,
    setExpanded,
    contentRef,
    children,
  }) => {
    const tabHeaders = useMemo(
      () =>
        tabs.map(({ stateKey }) => (
          <Tab key={stateKey} className="cms-tabs__tab main-tabs__tab">
            {fieldState[stateKey]}
          </Tab>
        )),
      [tabs]
    );

    return (
      <Tabs
        selectedTabClassName="main-tabs__tab--active"
        defaultIndex={defaultTabIndex}
        onSelect={onTabSelect}
      >
        <TabList className="main-tabs">{tabHeaders}</TabList>
        <div className="cms-tabs__content">
          {tabs.map(({ stateKey, field }) => (
            <TabPanel key={stateKey}>
              <MemoizedTabPanel
                key={stateKey}
                stateKey={stateKey}
                field={field}
                expanded={expanded}
                setExpanded={setExpanded}
                contentRef={contentRef}
              >
                {children}
              </MemoizedTabPanel>
            </TabPanel>
          ))}
        </div>
      </Tabs>
    );
  }
);

// Updated main component
function TextAreaFieldStateSandwich({
  children,
  templateKey,
  childClassName,
}: {
  children: React.ReactNode;
  templateKey: string;
  childClassName?: string;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const { getRangerSuggestions, getDefaultPrompt } =
    useTemplateStoreFromContext()(
      useShallow(state => ({
        getDefaultPrompt: state.getDefaultPrompt,
        getRangerSuggestions: state.getRangerSuggestions,
      }))
    );

  const { field } = useFieldValue(templateKey, null);
  const { field: managerData } = useFieldValue(
    templateKey,
    types.FieldState.Mgr
  );
  const { field: rangerData } = useFieldValue(
    templateKey,
    types.FieldState.RangerGenerated
  );
  const [tabIndex, setTabIndex] = useState(
    !field?.value && managerData?.value
      ? 1
      : !field?.value && rangerData?.value
        ? 2
        : 0
  );

  const [expanded, setExpanded] = useState(false);

  const [fetchingSuggestion, setFetchingSuggetions] = useState(false);
  const [suggestionsFetchError, setSuggestionsFetchError] = useState<
    string | null
  >(null);
  const [noSuggestionWasFound, setNoSuggestionWasFound] = useState(false);

  const [defaultPrompt, setDefaultPrompt] = useState<string | null>(null);
  const [promptOverride, setPromptOverride] = useState<string | null>(null);
  const [editPromptExpanded, setEditPromptExpanded] = useState(false);

  // Memoize tabs array with better dependency tracking
  const tabs: TabContent[] = useMemo(
    () => [
      { stateKey: "published", field },
      { stateKey: "mgr", field: managerData },
      { stateKey: "rangerGenerated", field: rangerData },
    ],
    [
      field?.value,
      field?.lastUpdated,
      managerData?.value,
      managerData?.lastUpdated,
      rangerData?.value,
      rangerData?.lastUpdated,
    ]
  );

  // Memoize default tab index
  const defaultTabIndex = useMemo(
    () =>
      !field?.value && managerData?.value
        ? 1
        : !field?.value && rangerData?.value
          ? 2
          : 0,
    [field?.value, managerData?.value, rangerData?.value]
  );

  useEffect(() => {
    if (defaultPrompt) return;

    getDefaultPrompt(capitalize(templateKey.split(".")[1])).then(prompt => {
      setDefaultPrompt(prompt);
    });
  }, [templateKey, defaultPrompt, getDefaultPrompt]);

  // Memoize the RangerSuggestionPromptEdit to prevent re-creation and focus loss
  const rangerSuggestionPromptEdit = useMemo(() => {
    const isRangerTab =
      tabIndex ===
      tabs.findIndex(({ stateKey }) => stateKey === "rangerGenerated");

    if (!isRangerTab) {
      return null;
    }

    return (
      <Accordion
        key="ranger-prompt-edit-accordion"
        elevation={0}
        disableGutters
        sx={{
          backgroundColor: "transparent",
          borderRadius: "4px",
          marginBottom: "8px",
          "&:before": {
            display: "none",
          },
        }}
        expanded={editPromptExpanded}
        onChange={() => setEditPromptExpanded(!editPromptExpanded)}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMoreIcon sx={{ fontSize: "16px", color: "#666" }} />
          }
          sx={{
            minHeight: "32px",
            padding: "0",
            borderRadius: "4px",
            "&.Mui-expanded": {
              minHeight: "32px",
            },
            "& .MuiAccordionSummary-content": {
              margin: "0",
              "&.Mui-expanded": {
                margin: "0",
              },
            },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: "11px",
            }}
          >
            Edit Sirius AI Prompt
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: "0" }}>
          <TextField
            multiline
            fullWidth
            minRows={3}
            maxRows={8}
            value={promptOverride || defaultPrompt}
            onChange={e => setPromptOverride(e.target.value)}
            placeholder="Enter your custom prompt here..."
            variant="outlined"
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "12px",
                lineHeight: 1.4,
                backgroundColor: "#fafbfc",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e0e0e0",
              },
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                {
                  borderColor: "#c0c0c0",
                },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  borderColor: "#1976d2",
                  borderWidth: "1px",
                },
            }}
          />
        </AccordionDetails>
      </Accordion>
    );
  }, [tabIndex, tabs, defaultPrompt, editPromptExpanded, promptOverride]);

  // Memoize ranger suggestion component
  const rangerSuggestionButton = useMemo(
    () =>
      tabIndex ===
        tabs.findIndex(({ stateKey }) => stateKey === "rangerGenerated") && (
        <div
          onClick={() => {
            setNoSuggestionWasFound(false);
            setFetchingSuggetions(true);

            const custopmPrompts = promptOverride
              ? [
                  {
                    fieldKey: capitalize(templateKey.split(".")[1]),
                    prompt: promptOverride,
                  },
                ]
              : undefined;

            getRangerSuggestions({
              keys: [templateKey.split(".")[1]],
              customPrompts: custopmPrompts,
              skipSave: true,
              ragLimits: 100,
            })
              .then(() => {
                if (!rangerData?.value) {
                  setNoSuggestionWasFound(true);
                }
                setFetchingSuggetions(false);
              })
              .catch(err => {
                setFetchingSuggetions(false);
                setSuggestionsFetchError(err.message);
              });
          }}
        >
          {noSuggestionWasFound && (
            <div className="text-gray-500">
              No Sirius AI suggestion was found for this field.
            </div>
          )}
          {suggestionsFetchError && (
            <div className="text-red-500">{suggestionsFetchError}</div>
          )}
          {fetchingSuggestion ? (
            <i className="rounded-btn__spinner in-blue-500" />
          ) : (
            <div className="in-blue-500" style={{ cursor: "pointer" }}>
              Get Sirius AI Suggestion
            </div>
          )}
        </div>
      ),
    [
      tabIndex,
      tabs,
      suggestionsFetchError,
      fetchingSuggestion,
      getRangerSuggestions,
      templateKey,
    ]
  );

  return (
    <div>
      <div data-cy="field-state-sandwich" className="cms-info-status__bg mb-16">
        <MemoizedTabs
          tabs={tabs}
          defaultTabIndex={defaultTabIndex}
          onTabSelect={setTabIndex}
          expanded={expanded}
          setExpanded={setExpanded}
          contentRef={contentRef}
        >
          {rangerSuggestionPromptEdit}
          {rangerSuggestionButton}
        </MemoizedTabs>
      </div>
      <div className={childClassName}>{children}</div>
    </div>
  );
}

export default TextAreaFieldStateSandwich;
